import {
  EXTENSION_MIMETYPE,
  Flex,
  useDownloadFile,
  useStyledTheme,
} from '@equifinance/shared';
import styled from 'styled-components';
import moment from 'moment';
import { DateFormatStringEnum } from '@equifinance/shared/utils';
import { PostOffer, useAccountContext } from '../../../contexts';
import useDocumentsApi from '../../../api/useDocumentsApi';
import { ChecklistDocumentStatus } from '../../../contexts/checklist';
import { HiddedA } from '../../Products/ProductComponents';

const DocumentItemContainer = styled(Flex)`${({ theme }) => `
  align-items: center;
  border-left: 12px solid ${theme.colours.neutral[7]};
  border-radius: ${theme.borderRadius[2]};
  background: ${theme.colours.neutral[9]};
  width: 100%;
  line-height: ${theme.fontSize[5]};
  color: ${theme.colours.neutral[2]};
`}`;

const DocumentInfoContainer = styled(Flex)`${({ theme }) => `
  padding: ${theme.padding[5]} ${theme.padding[3]};
  flex-grow: 1;
  align-items: center;
  overflow: hidden;
`}`;

const DocumentNameContainer = styled.div`${({ theme }) => `
  padding: ${theme.padding[1]} ${theme.padding[3]};
  flex-grow: 1;
  overflow: hidden;
`}`;

const DocumentName = styled.div`${({ theme }) => `
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: ${theme.fontSize[1]};
  font-weight: ${theme.fontWeight.bold};
`}`;

const DocumentTimeContainer = styled.div`${({ theme }) => `
  padding: ${theme.padding[1]} ${theme.padding[3]};
  border-left: 1px solid ${theme.colours.neutral[7]};
  font-size: ${theme.fontSize[1]};
  color: ${theme.colours.neutral[2]};
  flex-shrink: 0;
`}`;

const DocumentTimeLabel = styled.span`${({ theme }) => `
  font-weight: ${theme.fontWeight.bold};
`}`;

const DocumentTimeValue = styled.span``;

const DocumentAction = styled(Flex)`${({ theme }) => `
  justify-content: flex-end;
  align-items: center;
  padding: 0 ${theme.padding[3]};
  width: ${theme.taskEvidenceActionsWidth};
`}`;

const DownloadIcon = styled.i`${({ theme }) => `
  padding-right: ${theme.padding[1]};
  cursor: pointer;
`}`;

const DocumentStatusContainer = styled(Flex) <{ bgColor?: string }>`${({ theme, bgColor }) => `
  justify-content: flex-start;
  align-items: center;
  align-self: stretch;
  padding: ${theme.padding[6]} ${theme.padding[3]};
  background: ${bgColor};
  width: ${theme.taskEvidenceStatusWidth};
  gap: ${theme.padding[1]};
`}`;

const DocumentStatusLabel = styled.span`${({ theme }) => `
  font-weight: ${theme.fontWeight.bold};
  font-size: ${theme.fontSize[1]};
  text-align: left;
`}`;

function useBgColor(status: keyof typeof ChecklistDocumentStatus): string | undefined {
  const { colours } = useStyledTheme();

  switch (status) {
    case 'Cancelled': return colours.danger[2];
    case 'Uploaded': return colours.success[2];
    case 'Offered': return colours.success[2];
    default: return colours.neutral[8];
  }
}

function DocumentItemStatus({ status }: { status: keyof typeof ChecklistDocumentStatus }) {
  const bgStatusColor = useBgColor(status);
  const documentStatus = ChecklistDocumentStatus[status];

  return (
    <DocumentStatusContainer bgColor={bgStatusColor}>
      <DocumentStatusLabel>{documentStatus}</DocumentStatusLabel>
    </DocumentStatusContainer>
  );
}

type DocumentItemProps = {
  document: PostOffer['documents'][number]
}

export default function DocumentItem({ document }: DocumentItemProps) {
  const { id: accountId } = useAccountContext();
  const { status, fileName, uploadedDate } = document;
  const { getDocument } = useDocumentsApi();

  const {
    downloadFile,
    downloadFileRef,
  } = useDownloadFile();

  const downloadDocument = async () => {
    const { documentData, fileName, documentType } = await getDocument(accountId, document.documentId);
    downloadFile(fileName, documentData, EXTENSION_MIMETYPE[documentType]);
  };

  return (
    <DocumentItemContainer>
      <DocumentInfoContainer>
        <DocumentNameContainer>
          <DocumentName>{fileName}</DocumentName>
        </DocumentNameContainer>
        <DocumentTimeContainer>
          <DocumentTimeLabel>Uploaded:</DocumentTimeLabel>
          <DocumentTimeValue>{` ${moment(uploadedDate).format(DateFormatStringEnum.YYYYMMDDHHmmss)}`}</DocumentTimeValue>
        </DocumentTimeContainer>
      </DocumentInfoContainer>
      <DocumentItemStatus status={status} />
      <DocumentAction>
        <DownloadIcon className="mi-download" onClick={downloadDocument} />
        <HiddedA ref={downloadFileRef} />
      </DocumentAction>
    </DocumentItemContainer>
  );
}
